/**
 * GA4 Analytics Context for Storefront
 * Auto-initializes GA4 tracking based on merchant configuration
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

const GA4Context = createContext(null);

// Frozen no-op fallback used when a consumer calls useGA4() outside a
// GA4Provider. Declared at module scope so every call returns the SAME
// object reference — listing track* fns from useGA4() in a hook's
// dependency array is now safe (they won't change between renders).
const NOOP = () => {};
const NO_OP_GA4 = Object.freeze({
  initialized: false,
  config: null,
  trackEvent: NOOP,
  trackViewItem: NOOP,
  trackViewItemList: NOOP,
  trackAddToCart: NOOP,
  trackRemoveFromCart: NOOP,
  trackViewCart: NOOP,
  trackBeginCheckout: NOOP,
  trackAddShippingInfo: NOOP,
  trackAddPaymentInfo: NOOP,
  trackPurchase: NOOP,
  trackSearch: NOOP,
  trackSelectItem: NOOP,
  trackLogin: NOOP,
  trackSignUp: NOOP,
  setUserId: NOOP,
});

/**
 * GA4 Provider - Wraps storefront pages and provides tracking capabilities
 * Automatically initializes GA4 when store config is available
 */
export function GA4Provider({ children, store }) {
  const [initialized, setInitialized] = useState(false);
  const [config, setConfig] = useState(null);
  const location = useLocation();
  const lastTrackedPath = useRef(null);

  // Extract GA4 config from store settings
  useEffect(() => {
    if (!store?.settings?.ga4) return;

    const ga4Config = store.settings.ga4;

    if (ga4Config.enabled && ga4Config.measurementId) {
      setConfig({
        measurementId: ga4Config.measurementId,
        debugMode: ga4Config.debugMode || false,
        trackPageViews: ga4Config.trackPageViews !== false,
        trackEcommerce: ga4Config.trackEcommerce !== false,
        trackUserEngagement: ga4Config.trackUserEngagement !== false
      });
    }
  }, [store?.settings?.ga4]);

  // Initialize GA4 when config is available
  useEffect(() => {
    if (!config?.measurementId || initialized) return;

    try {
      ReactGA.initialize(config.measurementId, {
        gaOptions: {
          send_page_view: false // We'll handle page views manually
        },
        gtagOptions: {
          debug_mode: config.debugMode
        }
      });

      setInitialized(true);
      console.log(`[GA4] Initialized for ${store?.name || 'store'} with ID: ${config.measurementId}`);
    } catch (error) {
      console.error('[GA4] Initialization error:', error);
    }
  }, [config, initialized, store?.name]);

  // Track page views on route change
  useEffect(() => {
    if (!initialized || !config?.trackPageViews) return;
    if (lastTrackedPath.current === location.pathname) return;

    lastTrackedPath.current = location.pathname;

    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
      title: document.title
    });

    if (config.debugMode) {
      console.log(`[GA4] Page view: ${location.pathname}`);
    }
  }, [location.pathname, initialized, config?.trackPageViews, config?.debugMode]);

  // Generic event tracking
  const trackEvent = useCallback((eventName, params = {}) => {
    if (!initialized) return;

    try {
      ReactGA.event(eventName, params);

      if (config?.debugMode) {
        console.log(`[GA4] Event: ${eventName}`, params);
      }
    } catch (error) {
      console.error('[GA4] Event tracking error:', error);
    }
  }, [initialized, config?.debugMode]);

  // E-commerce: View item
  const trackViewItem = useCallback((product) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('view_item', {
      currency: product.currency || 'NGN',
      value: parseFloat(product.price),
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || '',
        price: parseFloat(product.price),
        quantity: 1
      }]
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: View item list
  const trackViewItemList = useCallback((products, listName = 'Products') => {
    if (!initialized || !config?.trackEcommerce || !products?.length) return;

    trackEvent('view_item_list', {
      item_list_name: listName,
      items: products.slice(0, 20).map((product, index) => ({
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || '',
        price: parseFloat(product.price),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Add to cart
  const trackAddToCart = useCallback((product, quantity = 1) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('add_to_cart', {
      currency: product.currency || 'NGN',
      value: parseFloat(product.price) * quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || '',
        price: parseFloat(product.price),
        quantity
      }]
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Remove from cart
  const trackRemoveFromCart = useCallback((product, quantity = 1) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('remove_from_cart', {
      currency: product.currency || 'NGN',
      value: parseFloat(product.price) * quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || '',
        price: parseFloat(product.price),
        quantity
      }]
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: View cart
  const trackViewCart = useCallback((cart) => {
    if (!initialized || !config?.trackEcommerce || !cart?.items?.length) return;

    trackEvent('view_cart', {
      currency: cart.currency || 'NGN',
      value: parseFloat(cart.total || 0),
      items: cart.items.map((item, index) => ({
        item_id: item.productId || item.id,
        item_name: item.name,
        item_category: item.category || '',
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Begin checkout
  const trackBeginCheckout = useCallback((cart) => {
    if (!initialized || !config?.trackEcommerce || !cart?.items?.length) return;

    trackEvent('begin_checkout', {
      currency: cart.currency || 'NGN',
      value: parseFloat(cart.total || 0),
      items: cart.items.map((item, index) => ({
        item_id: item.productId || item.id,
        item_name: item.name,
        item_category: item.category || '',
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Add shipping info
  const trackAddShippingInfo = useCallback((cart, shippingTier) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('add_shipping_info', {
      currency: cart.currency || 'NGN',
      value: parseFloat(cart.total || 0),
      shipping_tier: shippingTier,
      items: cart.items.map((item, index) => ({
        item_id: item.productId || item.id,
        item_name: item.name,
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Add payment info
  const trackAddPaymentInfo = useCallback((cart, paymentType) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('add_payment_info', {
      currency: cart.currency || 'NGN',
      value: parseFloat(cart.total || 0),
      payment_type: paymentType,
      items: cart.items.map((item, index) => ({
        item_id: item.productId || item.id,
        item_name: item.name,
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // E-commerce: Purchase
  const trackPurchase = useCallback((order) => {
    if (!initialized || !config?.trackEcommerce) return;

    trackEvent('purchase', {
      transaction_id: order.id || order.orderId,
      value: parseFloat(order.total),
      currency: order.currency || 'NGN',
      tax: parseFloat(order.tax || 0),
      shipping: parseFloat(order.shippingCost || 0),
      items: order.items.map((item, index) => ({
        item_id: item.productId || item.id,
        item_name: item.name,
        item_category: item.category || '',
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        index
      }))
    });
  }, [initialized, config?.trackEcommerce, trackEvent]);

  // User engagement: Search
  const trackSearch = useCallback((searchTerm) => {
    if (!initialized || !config?.trackUserEngagement) return;
    trackEvent('search', { search_term: searchTerm });
  }, [initialized, config?.trackUserEngagement, trackEvent]);

  // User engagement: Select item
  const trackSelectItem = useCallback((product, listName) => {
    if (!initialized || !config?.trackUserEngagement) return;

    trackEvent('select_item', {
      item_list_name: listName,
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: parseFloat(product.price)
      }]
    });
  }, [initialized, config?.trackUserEngagement, trackEvent]);

  // User: Login
  const trackLogin = useCallback((method = 'email') => {
    if (!initialized) return;
    trackEvent('login', { method });
  }, [initialized, trackEvent]);

  // User: Sign up
  const trackSignUp = useCallback((method = 'email') => {
    if (!initialized) return;
    trackEvent('sign_up', { method });
  }, [initialized, trackEvent]);

  // Set user ID
  const setUserId = useCallback((userId) => {
    if (!initialized) return;

    try {
      ReactGA.set({ user_id: userId });
      if (config?.debugMode) {
        console.log(`[GA4] User ID set: ${userId}`);
      }
    } catch (error) {
      console.error('[GA4] Set user ID error:', error);
    }
  }, [initialized, config?.debugMode]);

  // Memoize the context value so consumers that destructure don't see a new
  // object reference each render. Each track* fn is already useCallback-stable
  // with narrow deps; this just prevents the Provider's value prop from
  // forcing every consumer's React.memo / useEffect deps to re-evaluate.
  const value = useMemo(() => ({
    initialized,
    config,
    // Generic
    trackEvent,
    // E-commerce
    trackViewItem,
    trackViewItemList,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    // User engagement
    trackSearch,
    trackSelectItem,
    // User
    trackLogin,
    trackSignUp,
    setUserId,
  }), [
    initialized,
    config,
    trackEvent,
    trackViewItem,
    trackViewItemList,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    trackSearch,
    trackSelectItem,
    trackLogin,
    trackSignUp,
    setUserId,
  ]);

  return (
    <GA4Context.Provider value={value}>
      {children}
    </GA4Context.Provider>
  );
}

/**
 * Hook to access GA4 tracking methods
 */
export function useGA4() {
  const context = useContext(GA4Context);

  // Return the frozen module-level fallback (same reference across all calls)
  // when no provider is mounted. Previously a fresh object literal with fresh
  // arrow fns was returned each call — that turned every track* fn into a
  // new ref on every render, which silently turned consumer useEffects with
  // these fns in their dep arrays into infinite-fire loops.
  return context || NO_OP_GA4;
}

export default GA4Context;
